import { Checkbox, FormControl, Input, Slider } from '@mui/joy';

import { KeyboardEventHandler, ReactNode } from 'react';

import { Tooltip } from '@/design-system';
import { Text } from '@/design-system';

export default function SliderWithInput({
  title,
  description,
  value,
  min,
  max,
  step,
  onChange,
  onBlur,
  disabled,
  disabledMessage,
  showMinMax,
  autoFocus,
  blurOnEnter,
  inputPosition = 'top',
  checked = false,
  onCheckChange,
}: {
  title: ReactNode;
  description?: ReactNode;
  value: number | null;
  min: number;
  max: number;
  step: number;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  showMinMax?: boolean;
  autoFocus?: boolean;
  blurOnEnter?: boolean;
  inputPosition?: 'top' | 'right';
  checked?: boolean;
  onCheckChange?: (checked: boolean) => void;
}) {
  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;
    onCheckChange?.(newChecked);
  };

  // Determine if the slider is disabled based on the check state when in 'right' mode
  const sliderDisabled = onCheckChange && !checked;

  const inputSx = {
    width: 70,
    minHeight: 0,
    px: 0,
    py: '2px',
    outline: 'none',
    '& input': {
      textAlign: 'center',
      boxShadow: 'none !important',
      MozAppearance: 'textfield',
    },
    '& input::-webkit-outer-spin-button': {
      WebkitAppearance: 'none !important',
      margin: 0,
    },
    '& input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none !important',
      margin: 0,
    },
  };

  const titleSection = (
    <div className="flex flex-col gap-1">
      <Text weight="medium">{title}</Text>
      {description && (
        <Text variant="sm" className="text-quaternary">
          {description}
        </Text>
      )}
    </div>
  );

  const input = (
    <InputWithKeyDownAndDisabledParam
      title={title}
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={onChange}
      onBlur={onBlur}
      disabled={inputPosition === 'right' ? sliderDisabled : disabled}
      autoFocus={autoFocus}
      inputSx={inputSx}
      onKeyDown={
        blurOnEnter
          ? (e) => e.key === 'Enter' && e.currentTarget.blur()
          : undefined
      }
    />
  );

  const slider = (
    <SliderWithDisabledParam
      min={min}
      max={max}
      step={step}
      value={value}
      showMinMax={showMinMax}
      onChange={onChange}
      onBlur={onBlur}
      disabled={inputPosition === 'right' ? sliderDisabled : disabled}
    />
  );

  return (
    <FormControl sx={{ gap: 0 }}>
      {inputPosition === 'top' ? (
        <>
          <div className="flex items-center justify-between">
            {titleSection}
            {input}
          </div>
          <Tooltip title={disabledMessage}>
            <div>{slider}</div>
          </Tooltip>
        </>
      ) : (
        <>
          <div className="mb-1 flex items-center justify-between">
            {titleSection}
            {onCheckChange && (
              <Tooltip title={'Enable Parameter'}>
                <Checkbox
                  checked={checked}
                  onChange={handleCheckChange}
                  size="sm"
                />
              </Tooltip>
            )}
          </div>
          <Tooltip title={disabledMessage}>
            <div className="flex items-center">
              {slider}
              {input}
            </div>
          </Tooltip>
        </>
      )}
    </FormControl>
  );
}

// Extract SliderWithDisabledParam as a separate component
function SliderWithDisabledParam({
  min,
  max,
  step,
  value,
  showMinMax,
  onChange,
  onBlur,
  disabled,
}: {
  min: number;
  max: number;
  step: number;
  value: number | null;
  showMinMax?: boolean;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-1 items-center gap-6 px-[7px]">
      {showMinMax && <div>{min}</div>}
      <Slider
        min={min}
        max={max}
        step={step}
        value={value ?? 0}
        sx={{ py: 2, px: 2 }}
        slotProps={{
          rail: { sx: { left: -7, right: -7 } },
          track: {
            sx: {
              '&:before': {
                content: "''",
                position: 'absolute',
                left: -7,
                top: 0,
                bottom: 0,
                background: 'var(--Slider-trackBackground)',
                width: 10,
                borderTopLeftRadius: 'var(--Slider-trackRadius)',
                borderBottomLeftRadius: 'var(--Slider-trackRadius)',
              },
            },
          },
        }}
        onChange={(_, newValue) => {
          !Array.isArray(newValue) && onChange(newValue);
        }}
        onChangeCommitted={onBlur}
        disabled={disabled}
      />
      {showMinMax && <div>{max}</div>}
    </div>
  );
}

// Extract InputWithKeyDownAndDisabledParam as a separate component
function InputWithKeyDownAndDisabledParam({
  title,
  value,
  min,
  max,
  step,
  onChange,
  onBlur,
  disabled,
  autoFocus,
  onKeyDown,
  inputSx,
}: {
  title: ReactNode;
  value: number | null;
  min: number;
  max: number;
  step: number;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  inputSx: any;
}) {
  return (
    <Input
      data-testid={`${title}-input`}
      value={value?.toString() ?? ''}
      onBlur={(e) => {
        const newValue = Number.parseFloat(e.target.value);
        if (newValue < min) {
          onChange(min);
        } else if (newValue > max) {
          onChange(max);
        }
        onBlur?.();
      }}
      onKeyDown={onKeyDown}
      onChange={(e) => onChange(Number.parseFloat(e.target.value))}
      type="number"
      size="sm"
      slotProps={{
        input: {
          min,
          max,
          step,
        },
      }}
      sx={{
        ...inputSx,
        ml: 2,
      }}
      disabled={disabled}
      autoFocus={autoFocus}
    />
  );
}
